import { createSlice } from '@reduxjs/toolkit';
import { addNewQuickInvoice, changeInvoiceStatus, editInvoice, editQuickInvoice, getAllQuickInvoiceList, getAllQuickInvoiceListByStaff, getQuickInvoiceById, getQuickInvoiceByStaffById } from './quickInvoiceService';

const initialState = {
  allInvoiceList: {
    isInvoiceListLoading: null,
    isInvoiceListSuccess: null,
    invoiceListMessage: null,
    pageCount: 0,
    invoiceList: [],
  },

  allInvoiceListByStaff: {
    isInvoiceListLoading: null,
    isInvoiceListSuccess: null,
    invoiceListMessage: null,
    pageCount: 0,
    invoiceList: [],
  },

  newInvoice: {
    isInvoiceCreating: null,
    isInvoiceCreated: null,
    invoiceCreationMsg: null,
  },

  editedInvoice: {
    isEditingInvoice: null,
    isInvoiceEdited: null,
    invoiceEditedMsg: null
  },

  quickInvoiceDetails: {
    isInvoiceLoading: null,
    isInvoiceSuccess: null,
    invoice: null
  },

  quickInvoiceDetailsByStaff: {
    isInvoiceLoading: null,
    isInvoiceSuccess: null,
    invoiceByStaff: null
  },

  changeStatus: {
    isStatusLoading: null,
    isStatusSuccess: null,
  },
};

const quickInvoiceSlice = createSlice({
  name: 'quickInvoice',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // PENDING
    builder
      .addCase(getAllQuickInvoiceList.pending, (state) => {
        state.allInvoiceList = {
          ...initialState.allInvoiceList, // Reset to initial state
          isInvoiceListLoading: true,
          invoiceListMessage: 'Loading',
        };
      })
      .addCase(getAllQuickInvoiceList.rejected, (state, action) => {
        state.allInvoiceList = {
          ...initialState.allInvoiceList,
          isInvoiceListLoading: false,
          isInvoiceListSuccess: false,
          invoiceListMessage: action.payload || 'Error occurred while fetching invoiceList',
        };
      })
      .addCase(getAllQuickInvoiceList.fulfilled, (state, action) => {
        state.allInvoiceList = {
          ...initialState.allInvoiceList,
          isInvoiceListLoading: false,
          isInvoiceListSuccess: true,
          pageCount: action.payload.pageCount || 0,
          invoiceList: action.payload.data || [],
        };
      });

    builder
      .addCase(getAllQuickInvoiceListByStaff.pending, (state) => {
        state.allInvoiceListByStaff = {
          ...initialState.allInvoiceListByStaff, // Reset to initial state
          isInvoiceListLoading: true,
          invoiceListMessage: 'Loading',
        };
      })
      .addCase(getAllQuickInvoiceListByStaff.rejected, (state, action) => {
        state.allInvoiceListByStaff = {
          ...initialState.allInvoiceListByStaff,
          isInvoiceListLoading: false,
          isInvoiceListSuccess: false,
          invoiceListMessage: action.payload || 'Error occurred while fetching invoiceList',
        };
      })
      .addCase(getAllQuickInvoiceListByStaff.fulfilled, (state, action) => {
        state.allInvoiceListByStaff = {
          ...initialState.allInvoiceListByStaff,
          isInvoiceListLoading: false,
          isInvoiceListSuccess: true,
          pageCount: action.payload.pageCount || 0,
          invoiceList: action.payload.data || [],
        };
      });

    //Invoice creating
    builder
      .addCase(addNewQuickInvoice.pending, (state) => {
        state.newInvoice = {
          ...initialState.newInvoice, // Reset to initial state
          isInvoiceCreating: true,
          invoiceCreationMsg: 'Loading',
        };
      })

      // REJECTED
      .addCase(addNewQuickInvoice.rejected, (state, action) => {
        state.newInvoice = {
          ...initialState.newInvoice,
          isInvoiceCreating: false,
          isInvoiceCreated: false,
          invoiceCreationMsg: action.payload || 'Error occurred while creating the invoice.',
        };
      })

      // FULFILLED
      .addCase(addNewQuickInvoice.fulfilled, (state, action) => {
        state.newInvoice = {
          ...initialState.newInvoice,
          isInvoiceCreating: false,
          isInvoiceCreated: true,
          invoiceCreationMsg: action.payload || 'Successfully created the invoice.',
        };
      });

    //Invoice editing
    builder
      .addCase(editQuickInvoice.pending, (state) => {
        state.editedInvoice = {
          ...initialState.editedInvoice,
          isEditingInvoice: true,
          invoiceEditedMsg: 'Loading',
        };
      })

      .addCase(editQuickInvoice.rejected, (state, action) => {
        state.editedInvoice = {
          ...initialState.editedInvoice,
          isEditingInvoice: false,
          isInvoiceEdited: false,
          invoiceEditedMsg: action.payload || 'Error occurred while editing the invoice.',
        };
      })

      .addCase(editQuickInvoice.fulfilled, (state, action) => {
        state.editedInvoice = {
          ...initialState.editedInvoice,
          isEditingInvoice: false,
          isInvoiceEdited: true,
          invoiceEditedMsg: action.payload || 'Successfully edited the invoice.',
        };
      });

    //Invoice details
    builder
      .addCase(getQuickInvoiceById.pending, (state) => {
        state.quickInvoiceDetails = {
          ...initialState.quickInvoiceDetails,
          isInvoiceLoading: true,
        };
      })

      .addCase(getQuickInvoiceById.rejected, (state, action) => {
        state.quickInvoiceDetails = {
          ...initialState.quickInvoiceDetails,
          isInvoiceLoading: false,
          isInvoiceSuccess: false,
        };
      })

      .addCase(getQuickInvoiceById.fulfilled, (state, action) => {
        state.quickInvoiceDetails = {
          ...initialState.quickInvoiceDetails,
          isInvoiceLoading: false,
          isInvoiceSuccess: true,
          invoice: action.payload,
        };
      });

    //Invoice details by staff
    builder
      .addCase(getQuickInvoiceByStaffById.pending, (state) => {
        state.quickInvoiceDetailsByStaff = {
          ...initialState.quickInvoiceDetailsByStaff,
          isInvoiceLoading: true,
        };
      })

      .addCase(getQuickInvoiceByStaffById.rejected, (state, action) => {
        state.quickInvoiceDetailsByStaff = {
          ...initialState.quickInvoiceDetailsByStaff,
          isInvoiceLoading: false,
          isInvoiceSuccess: false,
        };
      })

      .addCase(getQuickInvoiceByStaffById.fulfilled, (state, action) => {
        state.quickInvoiceDetailsByStaff = {
          ...initialState.quickInvoiceDetailsByStaff,
          isInvoiceLoading: false,
          isInvoiceSuccess: true,
          invoiceByStaff: action.payload,
        };
      });

    //Change Invoice Status
    builder
      .addCase(changeInvoiceStatus.pending, (state) => {
        state.changeStatus = {
          ...initialState.changeStatus,
          isStatusLoading: true,
        };
      })

      .addCase(changeInvoiceStatus.rejected, (state, action) => {
        state.changeStatus = {
          ...initialState.changeStatus,
          isStatusLoading: false,
          isStatusSuccess: false,
        };
      })

      .addCase(changeInvoiceStatus.fulfilled, (state, action) => {
        state.changeStatus = {
          ...initialState.changeStatus,
          isStatusLoading: false,
          isStatusSuccess: true,
        };
      });
  },
});

export default quickInvoiceSlice.reducer;
